import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { USER_TYPE_OPTIONS, UserType, UserTypeOption } from './userTypes';
import './platform-stats-user-types.scss';

interface UserTypeRow {
  id: string;
  name: string | null;
  companyName: string | null;
  createdAt: string;
  userType: UserType;
  referralCount: number;
  /** Free days on the organisation seat subscription this user starts; null = no trial */
  organisationTrialDays: number | null;
  invitesThisMonth: number;
  /** Per-user monthly invite limit: 'default', a number, or null (unlimited) */
  monthlyInviteLimit: InviteLimitSetting;
}

/** null = unlimited */
type InviteLimit = number | null;
type InviteLimitSetting = InviteLimit | 'default';

const MAX_INVITE_LIMIT = 10000;
const DEFAULT_CUSTOM_LIMIT = 5;

const formatLimit = (limit: InviteLimit): string =>
  limit === null ? 'Unlimited' : String(limit);

const parseLimitDraft = (draft: string): number | undefined => {
  const n = Number(draft.trim());
  return draft.trim() !== '' &&
    Number.isInteger(n) &&
    n >= 0 &&
    n <= MAX_INVITE_LIMIT
    ? n
    : undefined;
};

interface UsersResponse {
  success: boolean;
  users: UserTypeRow[];
  userTypes: UserTypeOption[];
  defaultMonthlyInviteLimit?: InviteLimit;
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Admin "Users" tab: every registered user with their segment tag. The tag
 * defaults to Standard User at sign-up and is changed here; the Onboarding
 * tab's sign-up funnel attributes each sign-up to its referrer's tag.
 */
const UserTypesPanel: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [users, setUsers] = useState<UserTypeRow[]>([]);
  const [options, setOptions] = useState<UserTypeOption[]>(USER_TYPE_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'' | UserType>('');
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const [trialDrafts, setTrialDrafts] = useState<Record<string, string>>({});
  const [inviteDrafts, setInviteDrafts] = useState<Record<string, string>>({});
  const [defaultInviteLimit, setDefaultInviteLimit] = useState<InviteLimit>(5);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<UsersResponse>(
        '/platform-stats/user-types/users',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        setUsers(res.data.users);
        if (res.data.userTypes?.length) setOptions(res.data.userTypes);
        if (res.data.defaultMonthlyInviteLimit !== undefined) {
          setDefaultInviteLimit(res.data.defaultMonthlyInviteLimit);
        }
      }
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    load();
  }, [load]);

  const setUserType = async (userId: string, userType: UserType) => {
    const previous = users.find((u) => u.id === userId)?.userType;
    if (!previous || previous === userType) return;
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, userType } : u)),
    );
    setSavingIds((prev) => new Set(prev).add(userId));
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/platform-stats/user-types/users/${encodeURIComponent(userId)}`,
        { userType },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, userType: previous } : u)),
      );
      setError('Failed to save user type — change reverted');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const markSaving = (userId: string, saving: boolean) =>
    setSavingIds((prev) => {
      const next = new Set(prev);
      if (saving) next.add(userId);
      else next.delete(userId);
      return next;
    });

  const saveOrganisationTrial = async (userId: string) => {
    const draft = trialDrafts[userId];
    if (draft === undefined) return;
    const user = users.find((u) => u.id === userId);
    if (!user) return;
    const trimmed = draft.trim();
    const trialDays = trimmed === '' ? null : Number(trimmed);
    setTrialDrafts((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    if (trialDays === user.organisationTrialDays) return;
    if (
      trialDays !== null &&
      (!Number.isInteger(trialDays) || trialDays < 1 || trialDays > 365)
    ) {
      setError('Organisation trial must be a whole number of days (1–365)');
      return;
    }
    const previous = user.organisationTrialDays;
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, organisationTrialDays: trialDays } : u,
      ),
    );
    markSaving(userId, true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/platform-stats/user-types/users/${encodeURIComponent(userId)}/organisation-trial`,
        { trialDays },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, organisationTrialDays: previous } : u,
        ),
      );
      setError('Failed to save organisation trial — change reverted');
    } finally {
      markSaving(userId, false);
    }
  };

  const saveInviteLimit = async (userId: string, limit: InviteLimitSetting) => {
    const user = users.find((u) => u.id === userId);
    if (!user || limit === user.monthlyInviteLimit) return;
    const previous = user.monthlyInviteLimit;
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, monthlyInviteLimit: limit } : u,
      ),
    );
    markSaving(userId, true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await api.patch(
        `/platform-stats/user-types/users/${encodeURIComponent(userId)}/invite-limit`,
        { monthlyInviteLimit: limit },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, monthlyInviteLimit: previous } : u,
        ),
      );
      setError('Failed to save invite limit — change reverted');
    } finally {
      markSaving(userId, false);
    }
  };

  const commitInviteDraft = (userId: string) => {
    const draft = inviteDrafts[userId];
    if (draft === undefined) return;
    setInviteDrafts((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    const limit = parseLimitDraft(draft);
    if (limit === undefined) {
      setError(`Invite limit must be a whole number (0–${MAX_INVITE_LIMIT})`);
      return;
    }
    void saveInviteLimit(userId, limit);
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const u of users) c[u.userType] = (c[u.userType] ?? 0) + 1;
    return c;
  }, [users]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (typeFilter && u.userType !== typeFilter) return false;
      if (!q) return true;
      return (
        (u.name ?? '').toLowerCase().includes(q) ||
        (u.companyName ?? '').toLowerCase().includes(q) ||
        u.id.toLowerCase().includes(q)
      );
    });
  }, [users, search, typeFilter]);

  return (
    <div className='user-types-panel'>
      <div className='user-types-controls'>
        <input
          type='search'
          placeholder='Search name, company or id…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as '' | UserType)}
        >
          <option value=''>All types ({users.length})</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label} ({counts[o.value] ?? 0})
            </option>
          ))}
        </select>
        <span className='user-types-hint'>
          New sign-ups start as Standard User. Sign-ups are attributed to their
          referrer&apos;s type on the Onboarding tab.
        </span>
      </div>

      {error && <div className='msg error'>{error}</div>}
      {loading && <div className='loading'>Loading…</div>}

      {!loading && (
        <div className='user-types-table-wrap'>
          <table className='user-types-table'>
            <thead>
              <tr>
                <th>User</th>
                <th>Company</th>
                <th>Signed up</th>
                <th className='num'>Referrals</th>
                <th>User type</th>
                <th className='num'>Org trial (days)</th>
                <th>Invites / month</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7} className='empty'>
                    No users match.
                  </td>
                </tr>
              )}
              {visible.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className='user-cell'>
                      <span>{u.name || u.id}</span>
                      {u.name && <span className='user-id'>{u.id}</span>}
                    </div>
                  </td>
                  <td>{u.companyName ?? '—'}</td>
                  <td>{formatDate(u.createdAt)}</td>
                  <td className='num'>{u.referralCount}</td>
                  <td>
                    <select
                      className={`user-type-select type-${u.userType}`}
                      value={u.userType}
                      disabled={savingIds.has(u.id)}
                      aria-label={`User type for ${u.name || u.id}`}
                      onChange={(e) =>
                        setUserType(u.id, e.target.value as UserType)
                      }
                    >
                      {options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {savingIds.has(u.id) && (
                      <span className='saving'>Saving…</span>
                    )}
                  </td>
                  <td className='num'>
                    <input
                      type='number'
                      className='organisation-trial-input'
                      min={1}
                      max={365}
                      placeholder='none'
                      value={trialDrafts[u.id] ?? u.organisationTrialDays ?? ''}
                      disabled={savingIds.has(u.id)}
                      aria-label={`Organisation trial days for ${u.name || u.id}`}
                      onChange={(e) =>
                        setTrialDrafts((prev) => ({
                          ...prev,
                          [u.id]: e.target.value,
                        }))
                      }
                      onBlur={() => saveOrganisationTrial(u.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }}
                    />
                  </td>
                  <td>
                    <div className='invite-limit-cell'>
                      <span className='invite-usage'>
                        {u.invitesThisMonth} /{' '}
                        {formatLimit(
                          u.monthlyInviteLimit === 'default'
                            ? defaultInviteLimit
                            : u.monthlyInviteLimit,
                        )}
                      </span>
                      <select
                        value={
                          u.monthlyInviteLimit === 'default'
                            ? 'default'
                            : u.monthlyInviteLimit === null
                              ? 'unlimited'
                              : 'custom'
                        }
                        disabled={savingIds.has(u.id)}
                        aria-label={`Invite limit for ${u.name || u.id}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'default') saveInviteLimit(u.id, 'default');
                          else if (v === 'unlimited')
                            saveInviteLimit(u.id, null);
                          else
                            saveInviteLimit(
                              u.id,
                              defaultInviteLimit ?? DEFAULT_CUSTOM_LIMIT,
                            );
                        }}
                      >
                        <option value='default'>
                          Default ({formatLimit(defaultInviteLimit)})
                        </option>
                        <option value='custom'>Custom</option>
                        <option value='unlimited'>Unlimited</option>
                      </select>
                      {typeof u.monthlyInviteLimit === 'number' && (
                        <input
                          type='number'
                          className='invite-limit-input'
                          min={0}
                          max={MAX_INVITE_LIMIT}
                          value={inviteDrafts[u.id] ?? u.monthlyInviteLimit}
                          disabled={savingIds.has(u.id)}
                          aria-label={`Monthly invites for ${u.name || u.id}`}
                          onChange={(e) =>
                            setInviteDrafts((prev) => ({
                              ...prev,
                              [u.id]: e.target.value,
                            }))
                          }
                          onBlur={() => commitInviteDraft(u.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserTypesPanel;
